import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as saleService from '../../services/saleService';

export const createSale = createAsyncThunk(
  'sales/createSale',
  async (saleData, { rejectWithValue }) => {
    try {
      const res = await saleService.createSale(saleData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create sale');
    }
  }
);

export const fetchSales = createAsyncThunk(
  'sales/fetchSales',
  async (params, { rejectWithValue }) => {
    try {
      const res = await saleService.getSales(params);
      const body = res.data;
      return {
        sales: body.data || body.sales || [],
        totalPages: body.pagination?.totalPages || body.totalPages || 1,
        currentPage: body.pagination?.page || body.page || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch sales');
    }
  }
);

export const fetchSale = createAsyncThunk(
  'sales/fetchSale',
  async (id, { rejectWithValue }) => {
    try {
      const res = await saleService.getSale(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch sale');
    }
  }
);

export const voidSale = createAsyncThunk(
  'sales/voidSale',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const res = await saleService.voidSale(id, { reason });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to void sale');
    }
  }
);

export const refundSale = createAsyncThunk(
  'sales/refundSale',
  async ({ id, items, reason }, { rejectWithValue }) => {
    try {
      const res = await saleService.refundSale(id, { items, reason });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to refund sale');
    }
  }
);

export const fetchTodaySales = createAsyncThunk(
  'sales/fetchTodaySales',
  async (_, { rejectWithValue }) => {
    try {
      const res = await saleService.getTodaySales();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch today sales');
    }
  }
);

export const fetchCurrentShift = createAsyncThunk(
  'sales/fetchCurrentShift',
  async (_, { rejectWithValue }) => {
    try {
      const res = await saleService.getCurrentShift();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch current shift');
    }
  }
);

export const printReceipt = createAsyncThunk(
  'sales/printReceipt',
  async (id, { rejectWithValue }) => {
    try {
      const res = await saleService.printReceipt(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to print receipt');
    }
  }
);

export const emailReceipt = createAsyncThunk(
  'sales/emailReceipt',
  async ({ id, email }, { rejectWithValue }) => {
    try {
      const res = await saleService.emailReceipt(id, email);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to email receipt');
    }
  }
);

export const recordPayment = createAsyncThunk(
  'sales/recordPayment',
  async ({ saleId, paymentData }, { rejectWithValue }) => {
    try {
      const res = await saleService.recordPayment(saleId, paymentData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to record payment');
    }
  }
);

const initialState = {
  sales: [],
  sale: null,
  loading: false,
  error: null,
  todaySales: [],
  currentShift: null,
  totalPages: 1,
  currentPage: 1,
  receipt: null,
};

const saleSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    clearSale: (state) => {
      state.sale = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sale = action.payload.sale || action.payload;
        state.sales.unshift(state.sale);
      })
      .addCase(createSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = action.payload.sales || action.payload.data || action.payload;
        state.totalPages = action.payload.totalPages || action.payload.pages || 1;
        state.currentPage = action.payload.page || 1;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sale = action.payload.sale || action.payload;
      })
      .addCase(fetchSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(voidSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(voidSale.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.sale || action.payload;
        const idx = state.sales.findIndex((s) => s._id === updated._id);
        if (idx !== -1) state.sales[idx] = updated;
        if (state.sale?._id === updated._id) state.sale = updated;
      })
      .addCase(voidSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refundSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refundSale.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.sale || action.payload;
        const idx = state.sales.findIndex((s) => s._id === updated._id);
        if (idx !== -1) state.sales[idx] = updated;
        if (state.sale?._id === updated._id) state.sale = updated;
      })
      .addCase(refundSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTodaySales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodaySales.fulfilled, (state, action) => {
        state.loading = false;
        state.todaySales = action.payload.sales || action.payload.data || action.payload;
      })
      .addCase(fetchTodaySales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCurrentShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentShift.fulfilled, (state, action) => {
        state.loading = false;
        state.currentShift = action.payload.shift || action.payload;
      })
      .addCase(fetchCurrentShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(printReceipt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(printReceipt.fulfilled, (state, action) => {
        state.loading = false;
        state.receipt = action.payload;
      })
      .addCase(printReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(emailReceipt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(emailReceipt.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(emailReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(recordPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordPayment.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.sale || action.payload;
        const idx = state.sales.findIndex((s) => s._id === updated._id);
        if (idx !== -1) state.sales[idx] = updated;
        if (state.sale?._id === updated._id) state.sale = updated;
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSale, clearError, setPage } = saleSlice.actions;
export default saleSlice.reducer;
